import React, { useEffect, useState } from "react";
import TableActionButton, {
  TableActionButtonContainer,
} from "../TableComponent/TableActionButton";
import TrashIcon from "@src/assets/img/trash-icon.svg";
import DownloadIcon from "@src/assets/img/download-icon.svg";
import MagnifyIcon from "@src/assets/img/magnify-icon.svg";
import AlsoFoundBadge from "../AlsoFoundBadge";
import { ITrack } from "@src/types/track";
import DraggableRowsTable from "../TableComponent/DraggableRowsTable";
import { UnControlledAppInput } from "../AppInput";
import { usePublicS3Downloader } from "@src/hooks/useS3Library";
import { useNotification } from "@src/context/NotificationContext";
import { useFormattingSeconds } from "@src/hooks/useFormattingSeconds";
import UIModal from "../UIModal/UIModal";
import useModal from "@src/hooks/useModal";
import { UIButton } from "../UIButton";
import Spinner from "../Spinner";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { useParams } from "react-router-dom";
import { useRemoveTrackFromPlaylistAction } from "@src/queries/playlistQueries";
import {
  resetRemoveTrackFromPlaylist,
  setRemoveTrackFromPlaylistStatus,
  setTrackToRemove,
} from "@src/redux/slices/playlistDetailsSlice";
import PlayButton from "../MusicPlayer/PlayButton";
import { IPlaylistRecord, PlaylistStatus } from "@src/types/playlist";
import Tooltip from "../Tooltip";
import { useRole } from "@src/hooks/useAuth";
import TrackArtWorkImage from "../TrackArtWorkImage";
import { TagIcon } from "lucide-react";
import TrackTagsModal from "../TrackTagsModal/TrackTagsModal";
import TrackShazamModal from "../TrackShazamModal/TrackShazamModal";
import TrackCoverModal from "../TrackCoverModal/TrackCoverModal";
import { FEATURES } from "@src/constants";
import { theme } from "@src/theme";
import styled from "styled-components";
import EditIcon from "@src/assets/img/edit-icon.svg";

export type onUpdateTrackParams = {
  id: string;
  field: string;
  value: string;
};
export type PlaylistSongsListProps = {
  data: ITrack[];
  onOrderChange: (updateData: any) => void;
  onUpdateRow: ({ id, field, value }: onUpdateTrackParams) => void;
  onRowClick: (row: any) => void;
  handlePlayButtonClick: (track: ITrack) => void;
  playlistStatus: PlaylistStatus;
  playlistDetails: IPlaylistRecord | null;
  activeTrackInMix?: ITrack | null;
  /** The list belongs to a mood, where managers may edit title and artist. */
  isMoodFactory?: boolean;
};

const CoverButton = styled.button`
  position: relative;
  display: flex;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  .cover-edit-icon {
    position: absolute;
    right: 2px;
    bottom: 2px;
    width: 14px;
    height: 14px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  &:hover .cover-edit-icon,
  &:focus-visible .cover-edit-icon {
    opacity: 1;
  }
`;

const FieldInputWrapper = styled.div<{ $centerIcon?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $centerIcon }) =>
    $centerIcon ? "center" : "flex-start"};
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;

  .field-input-edit-icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }
  &:hover .field-input-edit-icon {
    opacity: 1;
  }
`;

const FieldInput = (props: {
  row: any;
  value: string | null;
  onUpdate: (value: string) => void;
  onToggleDrag: (isEditing: boolean) => void;
  disabled?: boolean;
}): React.JSX.Element => {
  const [inputMode, setInputMode] = useState(false);
  const [currentValue, setCurrentValue] = useState(props.value ?? "");

  useEffect(() => {
    if (!inputMode) {
      setCurrentValue(props.value ?? "");
    }
  }, [props.value, inputMode]);

  const toggleInputMode = (): void => {
    setInputMode(!inputMode);
    props.onToggleDrag(!inputMode);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCurrentValue(e.target.value);
  };

  const handleBlur = (): void => {
    const trimmedValue = (currentValue ?? "").trim();
    setCurrentValue(trimmedValue);
    setInputMode(false);
    props.onToggleDrag(false);
    const previousValue = props.value ?? "";
    if (trimmedValue !== previousValue) {
      props.onUpdate(trimmedValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      handleBlur();
    }
  };

  const isEmptyAndEditable = !(props.value ?? "").trim() && !props.disabled;

  return (
    <FieldInputWrapper
      $centerIcon={isEmptyAndEditable}
      style={{ cursor: props.disabled ? "default" : "pointer" }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!inputMode && !props.disabled) {
          toggleInputMode();
        }
      }}
    >
      {inputMode ? (
        <UnControlledAppInput
          blueStyle
          minHeight={55}
          name="title"
          placeholder=""
          label=""
          value={currentValue ?? ""}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          onFocus={() => {
            if (!inputMode) {
              toggleInputMode();
            }
          }}
        />
      ) : (
        <>
          {!isEmptyAndEditable && (
            <span
              style={{
                cursor: props.disabled ? "default" : "pointer",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minHeight: "20px",
                flex: 1,
                minWidth: 0,
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!inputMode && !props.disabled) {
                  toggleInputMode();
                }
              }}
            >
              {(props.value ?? "") ? (
                <Tooltip content={props.value ?? ""}>
                  {props.value ?? ""}
                </Tooltip>
              ) : (
                <span style={{ color: theme.colors.gray }}>-</span>
              )}
            </span>
          )}
          {!props.disabled && !(props.value ?? "").trim() && (
            <img
              src={EditIcon}
              alt="Edit"
              className="field-input-edit-icon"
              aria-hidden
            />
          )}
        </>
      )}
    </FieldInputWrapper>
  );
};

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  max-width: 200px;
`;

const TagPill = styled.span`
  background-color: ${theme.colors.yellow};
  color: #000;
  border: 1px solid ${theme.colors.yellow};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
`;

const TagsIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const PlaylistSongsList: React.FC<PlaylistSongsListProps> = ({
  playlistDetails,
  playlistStatus,
  handlePlayButtonClick,
  data,
  onOrderChange,
  onUpdateRow,
  onRowClick,
  activeTrackInMix,
  isMoodFactory = false,
}): React.JSX.Element => {
  const roleType = useRole();
  const { isOpen, openModal, closeModal } = useModal();
  const {
    isOpen: isTagsModalOpen,
    openModal: openTagsModal,
    closeModal: closeTagsModal,
  } = useModal();
  const [selectedTrackForTags, setSelectedTrackForTags] =
    useState<ITrack | null>(null);
  const {
    isOpen: isShazamModalOpen,
    openModal: openShazamModal,
    closeModal: closeShazamModal,
  } = useModal();
  const [selectedTrackForShazam, setSelectedTrackForShazam] =
    useState<ITrack | null>(null);
  const {
    isOpen: isCoverModalOpen,
    openModal: openCoverModal,
    closeModal: closeCoverModal,
  } = useModal();
  const [selectedTrackForCover, setSelectedTrackForCover] =
    useState<ITrack | null>(null);
  const dispatch = useAppDispatch();
  const { trackToRemove, removeTrackFromPlaylistStatus } = useAppSelector(
    (state) => state.playlistDetails
  );
  const { currentPlayingTrack: currentTrack, trackStatus } = useAppSelector(
    (state) => state.trackPlayer
  );
  const canEditTags = roleType === "admin" || roleType === "manager";
  const canSearchTrackDetails =
    roleType === "admin" && FEATURES.SEARCH_TRACK_DETAILS_ENABLED;
  // Managers can't edit track information (see PlaylistDetails.handleUpdateRow),
  // except a mood track's title and artist.
  const canEditTitleAndArtist = roleType !== "manager" || isMoodFactory;
  const canChangeTrackCover =
    FEATURES.TRACK_COVER_OVERRIDE_ENABLED && roleType !== "manager";

  useEffect(() => {
    if (selectedTrackForTags?.documentId && data.length > 0) {
      const updatedTrack = data.find(
        (t) => t.documentId === selectedTrackForTags.documentId
      );
      if (updatedTrack) {
        setSelectedTrackForTags(updatedTrack);
      }
    }
  }, [data, selectedTrackForTags?.documentId]);
  const [isDragDisabled, setIsDragDisabled] = useState(true);
  const handleToggleDrag = (isEditing: boolean): void => {
    if (
      !isEditing &&
      (playlistStatus === "draft" || playlistStatus === "ready")
    ) {
      setIsDragDisabled(false);
    } else {
      setIsDragDisabled(true);
    }
  };

  const hasPlaylistAction =
    playlistDetails?.playlistAction &&
    (playlistDetails?.playlistAction === "cut" ||
      playlistDetails?.playlistAction === "crossfade");

  useEffect(() => {
    if (
      playlistStatus === "draft" ||
      (playlistStatus === "ready" && !hasPlaylistAction)
    ) {
      setIsDragDisabled(false);
    } else {
      setIsDragDisabled(true);
    }
  }, [playlistStatus, playlistDetails]);
  const {
    downloadTrackById,
    isDownloading,
    error: errorDownloading,
  } = usePublicS3Downloader();
  const { addNotification } = useNotification();
  useEffect(() => {
    if (errorDownloading) {
      addNotification(errorDownloading, "error");
    }
  }, [errorDownloading]);
  const onRemoveClicked = (track: ITrack): void => {
    dispatch(setTrackToRemove(track));
    openModal();
  };
  const onCloseRemove = (): void => {
    dispatch(resetRemoveTrackFromPlaylist());
    closeModal();
  };

  const TagsWithTooltip = ({ row }: { row: ITrack }): React.JSX.Element => {
    const emotionArray =
      row.emotion && !Array.isArray(row.emotion)
        ? [row.emotion]
        : Array.isArray(row.emotion)
          ? row.emotion
          : [];
    const genreArray =
      row.genre && !Array.isArray(row.genre)
        ? [row.genre]
        : Array.isArray(row.genre)
          ? row.genre
          : [];

    const subGenreArray = (() => {
      const rawSubGenre = row.subGenre;
      if (!rawSubGenre) return [];
      if (typeof rawSubGenre === "string") {
        const trimmed = rawSubGenre.trim();
        return trimmed.length > 0
          ? [
              {
                documentId: trimmed,
                title: trimmed,
                description: "",
                color: "",
              },
            ]
          : [];
      }
      if (Array.isArray(rawSubGenre)) {
        return rawSubGenre;
      }
      return [rawSubGenre];
    })();

    const allTags = [
      ...(row.tags || []),
      ...emotionArray,
      ...genreArray,
      ...subGenreArray,
    ];

    const formatTagsForTooltip = () => {
      const sections = [];
      if (row.tags && Array.isArray(row.tags) && row.tags.length > 0) {
        sections.push(`Tags: ${row.tags.map((t) => t.title).join(", ")}`);
      }
      const emotion =
        row.emotion && !Array.isArray(row.emotion)
          ? row.emotion
          : Array.isArray(row.emotion) && row.emotion.length > 0
            ? row.emotion[0]
            : null;
      if (emotion && emotion.title) {
        sections.push(`Emotion: ${emotion.title}`);
      }
      const genre =
        row.genre && !Array.isArray(row.genre)
          ? row.genre
          : Array.isArray(row.genre) && row.genre.length > 0
            ? row.genre[0]
            : null;
      if (genre && genre.title) {
        sections.push(`Genre: ${genre.title}`);
      }
      const subGenre =
        row.subGenre && !Array.isArray(row.subGenre)
          ? row.subGenre
          : Array.isArray(row.subGenre) && row.subGenre.length > 0
            ? row.subGenre[0]
            : null;
      if (subGenre && typeof subGenre === "object" && subGenre.title) {
        sections.push(`Sub-genre: ${subGenre.title}`);
      }
      return sections.length > 0 ? sections.join("\n") : "No tags";
    };

    const tooltipContent = formatTagsForTooltip();

    if (!canEditTags) {
      return (
        <TagsContainer>
          {allTags.slice(0, 3).map((tag) => (
            <TagPill key={tag.documentId} title={tag.title}>
              {tag.title}
            </TagPill>
          ))}
          {allTags.length > 3 && <TagPill>+{allTags.length - 3}</TagPill>}
        </TagsContainer>
      );
    }

    return (
      <Tooltip
        content={tooltipContent}
        contentStyle={{
          whiteSpace: "pre-line",
          maxWidth: "300px",
        }}
      >
        <TagsIconContainer
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTrackForTags(row);
            openTagsModal();
          }}
        >
          <TagIcon size={18} style={{ color: theme.colors.yellow }} />
          <TagsContainer>
            {allTags.slice(0, 3).map((tag) => (
              <TagPill key={tag.documentId} title={tag.title}>
                {tag.title}
              </TagPill>
            ))}
            {allTags.length > 3 && <TagPill>+{allTags.length - 3}</TagPill>}
          </TagsContainer>
        </TagsIconContainer>
      </Tooltip>
    );
  };

  const columns = [
    {
      Header: "#",
      accessor: "display_row_index",
      width: "35px",
    },
    {
      Header: "",
      accessor: "albumCoverUrl",
      centerContent: true,
      Cell: ({ row }: { row: ITrack }): React.JSX.Element =>
        canChangeTrackCover ? (
          <Tooltip content="Change track image">
            <CoverButton
              type="button"
              aria-label="Change track image"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTrackForCover(row);
                openCoverModal();
              }}
            >
              <TrackArtWorkImage
                imageUrl={row.albumCoverUrl}
                $compressedUI={true}
              />
              <img className="cover-edit-icon" src={EditIcon} alt="" />
            </CoverButton>
          </Tooltip>
        ) : (
          <TrackArtWorkImage
            imageUrl={row.albumCoverUrl}
            $compressedUI={true}
          />
        ),
      width: "60px",
    },
    {
      Header: "Title",
      accessor: "title",
      Cell: ({
        row,
        value,
      }: {
        row: any;
        value: string;
      }): React.JSX.Element => (
        <FieldInput
          row={row}
          value={value}
          onUpdate={(newValue: string) =>
            onUpdateRow({ id: row.documentId, field: "title", value: newValue })
          }
          onToggleDrag={handleToggleDrag}
          disabled={!canEditTitleAndArtist}
        />
      ),
    },
    {
      Header: "Artist",
      accessor: "artist",
      Cell: ({
        row,
        value,
      }: {
        row: any;
        value: string;
      }): React.JSX.Element => (
        <FieldInput
          row={row}
          value={value}
          onUpdate={(newValue: string) =>
            onUpdateRow({
              id: row.documentId,
              field: "artist",
              value: newValue,
            })
          }
          onToggleDrag={handleToggleDrag}
          disabled={!canEditTitleAndArtist}
        />
      ),
    },
    {
      Header: "Album",
      accessor: "album",
      Cell: ({
        row,
        value,
      }: {
        row: any;
        value: string;
      }): React.JSX.Element => (
        <FieldInput
          row={row}
          value={value}
          onUpdate={(newValue: string) =>
            onUpdateRow({ id: row.documentId, field: "album", value: newValue })
          }
          onToggleDrag={handleToggleDrag}
          disabled={roleType === "manager"}
        />
      ),
    },
    {
      Header: "Label",
      accessor: "label",
      Cell: ({
        row,
        value,
      }: {
        row: any;
        value: string;
      }): React.JSX.Element => (
        <FieldInput
          row={row}
          value={value}
          onUpdate={(newValue: string) =>
            onUpdateRow({ id: row.documentId, field: "label", value: newValue })
          }
          onToggleDrag={handleToggleDrag}
          disabled={roleType === "manager"}
        />
      ),
    },
    {
      hidden: roleType !== "admin" && roleType !== "manager",
      Header: "ISRC",
      accessor: "isrc",
      Cell: ({
        row,
        value,
      }: {
        row: any;
        value: string;
      }): React.JSX.Element => (
        <FieldInput
          row={row}
          value={value}
          onUpdate={(newValue: string) =>
            onUpdateRow({ id: row.documentId, field: "isrc", value: newValue })
          }
          onToggleDrag={handleToggleDrag}
          disabled={roleType === "manager"}
        />
      ),
    },
    {
      Header: "BPM",
      accessor: "bpm",
    },
    {
      Header: "Key",
      accessor: "key",
    },
    {
      Header: "Bitrate",
      accessor: "bitrate",
    },
    {
      Header: "Duration",
      accessor: "duration",
      Cell: ({
        value,
        row,
      }: {
        value: number;
        row: ITrack;
      }): React.JSX.Element => {
        const textColor = row.isExcludedFromMix
          ? theme.colors.gray
          : theme.colors.yellow;
        if (value) {
          const { formatted } = useFormattingSeconds(value);
          return <span style={{ color: textColor }}>{formatted}</span>;
        }
        return <span style={{ color: textColor }}>00:00:00</span>;
      },
    },
    {
      Header: "Notes",
      accessor: "notes",
      Cell: ({
        row,
        value,
      }: {
        row: any;
        value: string;
      }): React.JSX.Element => (
        <FieldInput
          row={row}
          value={value}
          onUpdate={(newValue: string) =>
            onUpdateRow({ id: row.documentId, field: "notes", value: newValue })
          }
          onToggleDrag={handleToggleDrag}
          disabled={roleType === "manager"}
        />
      ),
    },
    {
      Header: "Also Found",
      accessor: "alsoFoundColor",
      centerContent: true,
      Cell: ({
        value,
        row,
      }: {
        value: string;
        row: ITrack;
      }): React.ReactNode => <AlsoFoundBadge row={row} value={value} />,
    },
    {
      hidden: roleType !== "admin" && roleType !== "manager",
      Header: "Tags",
      accessor: "tags",
      Cell: ({ row }: { row: ITrack }): React.JSX.Element => (
        <TagsWithTooltip row={row} />
      ),
      width: "200px",
      minWidth: "150px",
    },
    {
      Header: "Player",
      accessor: "audio.url",
      Cell: ({ row }: { row: ITrack }): React.JSX.Element => (
        <PlayButton
          loading={
            currentTrack?.documentId === row.documentId &&
            trackStatus === "loading"
          }
          isPlaying={
            currentTrack?.documentId === row.documentId &&
            trackStatus === "playing"
          }
          onClick={() => handlePlayButtonClick(row)}
        />
      ),
      width: "70px",
      minWidth: "60px",
      maxWidth: "90px",
    },
    {
      Header: "Actions",
      accessor: "actions",
      centerContent: true,
      width: "90px",
      minWidth: "80px",
      maxWidth: "110px",
      Cell: ({ row }: { row: ITrack }): React.JSX.Element => (
        <TableActionButtonContainer style={{ justifyContent: "center" }}>
          {canSearchTrackDetails && (
            <TableActionButton
              alt="search track details"
              $tooltipContent="Search Track Details"
              iconSrc={MagnifyIcon}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTrackForShazam(row);
                openShazamModal();
              }}
            />
          )}
          <TableActionButton
            disabled={
              (playlistDetails?.playlistStatus !== "draft" &&
                playlistDetails?.playlistStatus !== "ready") ||
              hasPlaylistAction ||
              removeTrackFromPlaylistStatus !== "idle"
            }
            onClick={(e) => {
              e.stopPropagation();
              onRemoveClicked(row);
            }}
            alt="delete"
            iconSrc={TrashIcon}
          />
          <TableActionButton
            alt="download"
            disabled={isDownloading}
            iconSrc={DownloadIcon}
            onClick={(e) => {
              e.stopPropagation();
              if (row?.audio?.url) {
                downloadTrackById(row?.documentId);
              } else {
                alert("No audio URL found");
              }
            }}
          />
        </TableActionButtonContainer>
      ),
    },
  ];
  const removeTrackMutation = useRemoveTrackFromPlaylistAction();
  const playlistId = useParams().playlistId;
  const onConfirm = async (): Promise<void> => {
    dispatch(setRemoveTrackFromPlaylistStatus("loading"));
    if (playlistId && trackToRemove?.documentId)
      await removeTrackMutation.mutateAsync(
        { playlistId, trackId: trackToRemove?.documentId },
        {
          onSuccess: () => {
            closeModal();
            addNotification("Track removed successfully", "success");
            dispatch(setRemoveTrackFromPlaylistStatus("success"));
          },
          onError: () => {
            closeModal();
            addNotification("Failed to remove track", "error");
            dispatch(setRemoveTrackFromPlaylistStatus("error"));
          },
          onSettled: () => {
            setTimeout(() => {
              dispatch(resetRemoveTrackFromPlaylist());
              dispatch(setRemoveTrackFromPlaylistStatus("idle"));
            }, 1000);
          },
        }
      );
  };
  return (
    <>
      <UIModal
        actions={[
          <UIButton
            disabled={removeTrackFromPlaylistStatus !== "idle"}
            key="cancel"
            onClick={onCloseRemove}
            className="secondary"
          >
            Cancel
          </UIButton>,
          <UIButton
            disabled={removeTrackFromPlaylistStatus !== "idle"}
            key="delete"
            onClick={onConfirm}
            variant="dangerOutline"
          >
            Delete
          </UIButton>,
        ]}
        isOpen={isOpen}
        title="Remove Track"
        onClose={closeModal}
      >
        <p
          style={{
            maxWidth: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Are you sure you want to remove the track <br />
          <Tooltip content={trackToRemove?.title ?? ""}>
            <strong>{trackToRemove?.title ?? ""}</strong>
          </Tooltip>
          <br /> from this playlist?
        </p>
        {removeTrackFromPlaylistStatus === "loading" && <Spinner small />}
      </UIModal>
      <TrackTagsModal
        isOpen={isTagsModalOpen}
        onClose={() => {
          closeTagsModal();
          setSelectedTrackForTags(null);
        }}
        track={selectedTrackForTags}
        onTrackUpdate={(updatedTrack) => {
          setSelectedTrackForTags(updatedTrack);
        }}
        source="playlist"
      />
      {canSearchTrackDetails && (
        <TrackShazamModal
          isOpen={isShazamModalOpen}
          onClose={() => {
            closeShazamModal();
            setSelectedTrackForShazam(null);
          }}
          track={selectedTrackForShazam}
        />
      )}
      {canChangeTrackCover && (
        <TrackCoverModal
          isOpen={isCoverModalOpen}
          onClose={() => {
            closeCoverModal();
            setSelectedTrackForCover(null);
          }}
          track={selectedTrackForCover}
          curatorId={playlistDetails?.curator?.documentId}
        />
      )}
      <DraggableRowsTable
        activeRow={activeTrackInMix ?? currentTrack}
        rowIdentifier="documentId"
        onRowClick={onRowClick}
        $resizableColumns
        $isDragDisabled={isDragDisabled}
        onOrderChange={onOrderChange}
        $compactUI={false}
        columns={columns}
        data={data}
      />
    </>
  );
};

export default PlaylistSongsList;
